import { Box, CircularProgress, Button, Card, Typography, Input, Stack, useTheme, Link } from '@mui/joy';
import React, { useState, useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BubbleChart } from '@mui/icons-material';
import { useMediaQuery } from '@mui/material';
import Home from '../pages/footage/Home';
import { getApiUrl } from '../utils/api';
const AuthContext = React.createContext();

const isRussianDomain = window.location.hostname === 'chat.acceptancegroup.org';

export const useAuth = () => {
    return useContext(AuthContext);
};

function convertUTCDateToLocalDate(date) {
    var newDate = new Date(date.getTime() + date.getTimezoneOffset() * 60 * 1000);

    var offset = date.getTimezoneOffset() / 60;
    var hours = date.getHours();

    newDate.setHours(hours - offset);

    return newDate;
}

function formatExpirationDate(expirationDate) {
    const utcDate = new Date(expirationDate);
    const localDate = convertUTCDateToLocalDate(utcDate);
    return localDate.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
}

const AuthProvider = ({ children, customLoad }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [unauthorized, setUnauthorized] = useState(false);
    const location = useLocation();
    const [checkRateLimit, setCheckRateLimit] = useState({
        voice: 0,
        chat: 0,
        characters: 0,
        canImageGenerate: false
    });

    const [isReg, setIsReg] = useState(false);

    const themeSize = useTheme();
    const isMobile = useMediaQuery(themeSize.breakpoints.down('sm'));

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loadingId, setLoadingId] = useState(false);

    useEffect(() => {
        const checkToken = async () => {
            const urlParams = new URLSearchParams(location.search);
            const tokenFromUrl = urlParams.get('token');

            if (tokenFromUrl) {
                localStorage.setItem('screwltd-token', tokenFromUrl);
                await fetchUserProfile(tokenFromUrl);
            } else {
                const token = localStorage.getItem('screwltd-token');
                if (token) {
                    await fetchUserProfile(token);
                } else {
                    setUnauthorized(true);
                    setLoading(false);
                }
            }
        };

        checkToken();
    }, [location]);

    const handleAuth = async (isReg) => {
        setLoadingId(true);
        const apiUrl = isReg ? getApiUrl('/v3/auth/register') : getApiUrl('/v3/auth/login');
        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            if (!response.ok) throw new Error('Login failed');

            const data = await response.json();
            localStorage.setItem('screwltd-token', data.token);
            await fetchUserProfile(data.token);
        } catch (error) {
            console.error('An error occurred during login:', error);
        }
        finally {
            setLoadingId(false);
        }
    }

    const setRateLimit = async (user) => {
        let chatLimit, voiceLimit, characterLimit, canImageGenerate;

        const now = new Date().toISOString();
        const isSmartActive = user.ai.smart.duration && new Date(user.ai.smart.duration) > new Date(now);
        let plan = user.ai.smart.type;

        if (!isSmartActive || plan === 'Free' || !plan) {
            plan = 'Free';
        }

        switch (plan) {
            case 'Free':
                chatLimit = 30;
                voiceLimit = 0;
                characterLimit = 300;
                canImageGenerate = false;
                break;
            case 'Smart':
                chatLimit = 70;
                voiceLimit = 10;
                characterLimit = 500;
                canImageGenerate = true;
                break;
            case 'Ultimate':
                chatLimit = 200;
                voiceLimit = 50;
                characterLimit = 1000;
                canImageGenerate = true;
                break;
            case 'Advanced':
                chatLimit = 1000;
                voiceLimit = 100;
                characterLimit = Infinity;
                canImageGenerate = true;
                break;
            case 'Enterprise':
                chatLimit = Infinity;
                voiceLimit = Infinity;
                characterLimit = Infinity;
                canImageGenerate = true;
                break;
            default:
                chatLimit = 10;
                voiceLimit = 0;
                characterLimit = 300;
                canImageGenerate = false;
                break;
        }

        setCheckRateLimit({
            voice: voiceLimit,
            characters: characterLimit,
            chat: chatLimit,
            canImageGenerate: canImageGenerate
        });
    }

    const fetchUserProfile = async (token, retries = 3) => {
        try {
            const response = await fetch(getApiUrl('/v3/auth/profile?service=ai'), {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (!response.ok) {
                if (response.status === 401) throw new Error('Token validation failed');
                if (retries > 0) {
                    console.log('Retrying request...');
                    setTimeout(() => fetchUserProfile(token, retries - 1), 2000);
                    return;
                }
                const errorData = await response.json();
                if (errorData.error === "Email not verified") {
                    redirectToAuthWithToken(token);
                    return;
                }
                throw new Error('Request failed after retries');
            }

            const data = await response.json();
            data.jwt_token = token;
            if (window.__TAURI_INTERNALS__) {
                data.client = "Capricorn: For Windows";
            }
            setUser(data);
            setRateLimit(data);
            setLoading(false);
            setUnauthorized(false);
            await refreshToken(data.jwt_token);
        } catch (error) {
            setLoading(false);
            if (error.message === 'Token validation failed') {
                localStorage.removeItem('screwltd-token');
                setUnauthorized(true);
            } else {
                console.error('An error occurred:', error);
                localStorage.removeItem('screwltd-token');
                redirectToAuth();
            }
        }
    };

    const refreshToken = async (token) => {
        try {
            const response = await fetch(getApiUrl('/v3/auth/refresh'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });

            if (!response.ok) throw new Error('Failed to refresh token');

            const { token: newToken } = await response.json();
            localStorage.setItem('screwltd-token', newToken);

            setUser((prevUser) => ({
                ...prevUser,
                jwt_token: newToken,
            }));
        } catch (error) {
            console.error('An error occurred while refreshing the token:', error);
        }
    };

    return (
        <AuthContext.Provider value={{ user, loading, checkRateLimit }}>
            {loading || customLoad ? (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 9999,
                    }}
                >
                    <CircularProgress color="neutral" size="md" variant="plain" />
                </Box>
            ) : unauthorized ? (
                <>
                    {window.__TAURI_INTERNALS__ ? (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '100vh',
                                flexDirection: 'column',
                                gap: 2,
                            }}
                        >
                            {!isMobile && (
                                <iframe style={{ position: 'absolute', width: '100%', height: '100%', top: 0, bottom: 0, left: 0, right: 0, border: 'none' }} src="./3d.html" />
                            )}
                            <motion.div
                                initial={{ opacity: isMobile ? 1 : 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: isMobile ? 0 : 3, duration: isMobile ? 0 : 0.5 }}
                            >
                                <Card variant={isMobile ? 'plain' : 'outlined'} color='primary' sx={{ borderRadius: 20, p: 4, textAlign: 'center', maxWidth: '310px' }}>
                                    <Typography color='primary' level='title-lg'><BubbleChart sx={{ height: 36, width: 36, mb: -1 }} /></Typography>
                                    <Stack spacing={0.5}>
                                        <Typography level='title-lg'>Capricorn AI</Typography>
                                        <Typography level='body-sm'>Unlock the unlimited possibilities of Capricorn AI by logging in with your SCREW: ID!</Typography>
                                    </Stack>
                                    <Input
                                        disabled={loadingId}
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        sx={{ overflow: 'hidden', borderColor: '#12467B' }}
                                        color='primary'
                                        placeholder='E-Mail...'
                                    />
                                    <Input
                                        disabled={loadingId}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        sx={{ overflow: 'hidden', borderColor: '#12467B' }}
                                        type="password"
                                        color='primary'
                                        placeholder='Password...'
                                    />
                                    <Button
                                        loading={loadingId}
                                        variant="soft"
                                        sx={{ width: '100%', borderRadius: 250 }}
                                        onClick={() => handleAuth(isReg)}
                                    >
                                        {!isReg ? 'LOG IN' : 'GO ON A TRIP'}
                                    </Button>
                                    {!isReg ? (
                                        <Typography sx={{ mt: 2 }} level='body-sm'>Don't have an account? <Link onClick={() => setIsReg(!isReg)}>Create one</Link></Typography>
                                    ) : (
                                        <Typography sx={{ mt: 2 }} level='body-sm'>Already registered? <Link onClick={() => setIsReg(!isReg)}>Sign in</Link></Typography>
                                    )}
                                </Card>
                            </motion.div>
                        </Box>
                    ) : (
                        <Home />
                    )}
                </>
            ) : user?.ban ? (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100vh',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Card variant='outlined' color='danger' sx={{ borderRadius: 20, p: 4, textAlign: 'center', maxWidth: '310px' }}>
                        <Typography color='danger' level='title-lg'><BubbleChart sx={{ height: 36, width: 36, mb: -1 }} /></Typography>
                        <Typography level='title-lg'>ACCESS DENIED</Typography>
                        <Typography sx={{ textAlign: 'left' }} level='body-sm'>You have received an account block due to:<br /><b>{user.ban.cause ?? "Without giving a reason."}</b></Typography>
                        <Typography sx={{ textAlign: 'left' }} level='body-sm'>Your access will be restored:<br /><b>{formatExpirationDate(user.ban.expiration_date)}</b></Typography>
                        <Button
                            variant="soft"
                            color='danger'
                            sx={{ width: '100%', borderRadius: 250 }}
                            onClick={() => { location.href = "mailto:support@screwltd.com"; }}
                        >
                            Contact support
                        </Button>
                    </Card>
                </Box>
            ) : (
                (children)
            )
            }
        </AuthContext.Provider >
    );
};

export const redirectToAuth = () => {
    if (isRussianDomain) {
        window.location.href = `https://auth.acceptancegroup.org/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.acceptancegroup.org%2F&scope=token`;
    } else {
        window.location.href = `https://auth.screwltd.com/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.screwltd.com%2F&scope=token`;
    }
};

export const redirectToAuthWithToken = (token) => {
    if (isRussianDomain) {
        window.location.href = `https://auth.acceptancegroup.org/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.acceptancegroup.org%2F&scope=token&token=${token}`;
    } else {
        window.location.href = `https://auth.screwltd.com/oauth2/authorize?client_id=e4fb02ed-3bf7-41ff-861e-50b0fab21481&redirect_uri=https%3A%2F%2Fchat.screwltd.com%2F&scope=token&token=${token}`;
    }
};

export const logout = () => {
    localStorage.removeItem('screwltd-token');
    if (!window.__TAURI_INTERNALS__) {
        redirectToAuth();
    }
    else {
        window.location.reload(false);
    }
};

export default AuthProvider;
